// Persistent Custom Vocabulary Storage (Per-Player Account)
// Allows each player to keep their own customized Kabyle words and translations.
// Supported with multi-tier storage (In-Memory, LocalStorage, IndexedDB, and Server Backup with Auto-Healing).

import { ALL_UNIQUE_WORDS } from '../data/gameData';
import { getActivePlayerId } from './saveManager';

export interface CustomWordItem {
  id?: string;
  kab: string;
  fr: string;
  type?: string;
  icon?: string;
  gender?: 'm' | 'f';
  grammaticalNumber?: 'sg' | 'pl';
  pluralKab?: string;
  singularKab?: string;
  variants?: string;
  conjugationAccompli?: string;
  conjugationInaccompli?: string;
  playerId?: string;
  createdAt?: number;
  updatedAt?: number;
}

export const MAX_CUSTOM_WORDS = 50;

const LEGACY_STORAGE_KEY_V1 = 'kabyleGame_customWords';
const IDB_NAME = 'tamazight_player_vocab_db';
const IDB_STORE = 'player_vocab_store';

// In-memory cache keyed by playerId
const inMemoryCache: Record<string, CustomWordItem[]> = {};
const isAutoHealingServer: Record<string, boolean> = {};

function getPlayerLocalStorageKey(playerId: string): string {
  return `tamazight_custom_vocabulary_v2_${playerId}`;
}

export function resolveCurrentPlayerId(targetPlayerId?: string): string {
  if (targetPlayerId && targetPlayerId.trim()) {
    return targetPlayerId.trim();
  }
  return getActivePlayerId();
}

let cachedAmawalSet: Set<string> | null = null;
let lastAllWordsCount = 0;

function getAmawalNormSet(): Set<string> {
  if (!cachedAmawalSet || lastAllWordsCount !== ALL_UNIQUE_WORDS.length) {
    cachedAmawalSet = new Set(ALL_UNIQUE_WORDS.map((w) => w.kab.trim().toLowerCase()));
    lastAllWordsCount = ALL_UNIQUE_WORDS.length;
  }
  return cachedAmawalSet;
}

/**
 * Filter out any word that is already part of the core Amawal dictionary
 */
export function isWordAlreadyInAmawal(kabyleWord: string): boolean {
  if (!kabyleWord) return false;
  const norm = kabyleWord.trim().toLowerCase();
  return getAmawalNormSet().has(norm);
}

function filterOutListedWords(words: CustomWordItem[]): CustomWordItem[] {
  if (!Array.isArray(words)) return [];
  const amawalNormSet = getAmawalNormSet();
  return words.filter((w) => {
    if (!w || !w.kab) return false;
    return !amawalNormSet.has(w.kab.trim().toLowerCase());
  });
}

// ==========================================
// IndexedDB Persistence Helpers
// ==========================================
function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not supported'));
    }
    const request = indexedDB.open(IDB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getStoredPlayerVocabFromIDB(playerId: string): Promise<CustomWordItem[] | null> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(playerId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

export async function setStoredPlayerVocabInIDB(playerId: string, words: CustomWordItem[]): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(words, playerId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('Could not store player vocabulary in IndexedDB:', e);
  }
}

export async function clearStoredPlayerVocabFromIDB(playerId: string): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.delete(playerId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('Could not clear player vocabulary from IndexedDB:', e);
  }
}

// ==========================================
// Core Vocabulary Retrieval (per Player Account)
// ==========================================
export function getCustomWords(targetPlayerId?: string): CustomWordItem[] {
  if (typeof window === 'undefined') return [];
  const playerId = resolveCurrentPlayerId(targetPlayerId);

  // 1. Check in-memory cache
  if (inMemoryCache[playerId]) {
    const cleaned = filterOutListedWords(inMemoryCache[playerId]);
    inMemoryCache[playerId] = cleaned;
    return cleaned;
  }

  // 2. Check localStorage for this player
  const key = getPlayerLocalStorageKey(playerId);
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const cleaned = filterOutListedWords(parsed);
        inMemoryCache[playerId] = cleaned;
        return cleaned;
      }
    }
  } catch (e) {
    console.warn('Could not read player vocab from localStorage:', e);
  }

  // 3. Check legacy migration (v1 global words -> assign to first active player)
  try {
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY_V1);
    if (legacyRaw) {
      const legacyParsed = JSON.parse(legacyRaw);
      if (Array.isArray(legacyParsed) && legacyParsed.length > 0) {
        const migrated: CustomWordItem[] = filterOutListedWords(
          legacyParsed.map((w: any) => ({
            ...w,
            playerId,
          }))
        );
        inMemoryCache[playerId] = migrated;
        localStorage.setItem(key, JSON.stringify(migrated));
        setStoredPlayerVocabInIDB(playerId, migrated);
        return migrated;
      }
    }
  } catch (e) {
    // Ignore legacy migration error
  }

  inMemoryCache[playerId] = [];
  return [];
}

/**
 * Get all custom words from all players locally (used for full system backup)
 */
export function getAllLocalCustomWords(): CustomWordItem[] {
  const allWordsMap = new Map<string, CustomWordItem>();
  if (typeof window === 'undefined') return [];

  // Check all localStorage keys
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('tamazight_custom_vocabulary_v2_')) {
        const val = localStorage.getItem(k);
        if (val) {
          const list = JSON.parse(val);
          if (Array.isArray(list)) {
            list.forEach((w) => {
              if (w && w.kab) allWordsMap.set(w.kab.toLowerCase().trim(), w);
            });
          }
        }
      }
    }
  } catch {}

  // Check in-memory caches
  Object.values(inMemoryCache).forEach((wordsList) => {
    wordsList.forEach((w) => {
      if (w && w.kab) allWordsMap.set(w.kab.toLowerCase().trim(), w);
    });
  });

  return Array.from(allWordsMap.values());
}

// ==========================================
// Save Word (Add or Update) in Player's Account
// ==========================================
export function saveCustomWord(
  word: Omit<CustomWordItem, 'createdAt' | 'updatedAt' | 'playerId'> & { id?: string },
  targetPlayerId?: string
): { success: boolean; word?: CustomWordItem; error?: string } {
  const playerId = resolveCurrentPlayerId(targetPlayerId);
  const trimmedKab = word.kab.trim();
  const trimmedFr = word.fr.trim();

  if (!trimmedKab || !trimmedFr) {
    return { success: false, error: 'Le mot kabyle et sa traduction française sont obligatoires.' };
  }

  if (isWordAlreadyInAmawal(trimmedKab)) {
    return { success: false, error: `« ${trimmedKab} » est déjà présent dans le dictionnaire officiel.` };
  }

  const currentWords = getCustomWords(playerId);

  // Check duplication
  const existingIndex = currentWords.findIndex(
    (w) => (word.id && w.id === word.id) || w.kab.trim().toLowerCase() === trimmedKab.toLowerCase()
  );

  const now = Date.now();
  let updatedWord: CustomWordItem;

  const finalPluralKab = word.pluralKab !== undefined ? (word.pluralKab.trim() || undefined) : undefined;
  const finalSingularKab = word.singularKab !== undefined ? (word.singularKab.trim() || undefined) : undefined;
  const finalVariants = word.variants !== undefined ? (word.variants.trim() || undefined) : undefined;
  const finalConjugationAccompli = word.conjugationAccompli !== undefined ? (word.conjugationAccompli.trim() || undefined) : undefined;
  const finalConjugationInaccompli = word.conjugationInaccompli !== undefined ? (word.conjugationInaccompli.trim() || undefined) : undefined;

  if (existingIndex >= 0) {
    updatedWord = {
      ...currentWords[existingIndex],
      kab: trimmedKab,
      fr: trimmedFr,
      icon: word.icon?.trim() || '📝',
      type: word.type?.trim() || currentWords[existingIndex].type || 'Personnalisé',
      gender: word.gender,
      grammaticalNumber: word.grammaticalNumber,
      pluralKab: finalPluralKab,
      singularKab: finalSingularKab,
      variants: finalVariants,
      conjugationAccompli: finalConjugationAccompli,
      conjugationInaccompli: finalConjugationInaccompli,
      playerId,
      updatedAt: now,
    };
    currentWords[existingIndex] = updatedWord;
  } else {
    updatedWord = {
      id: word.id || `custom_${playerId}_${now}_${Math.random().toString(36).substr(2, 6)}`,
      kab: trimmedKab,
      fr: trimmedFr,
      icon: word.icon?.trim() || '📝',
      type: word.type?.trim() || 'Personnalisé',
      gender: word.gender,
      grammaticalNumber: word.grammaticalNumber,
      pluralKab: finalPluralKab,
      singularKab: finalSingularKab,
      variants: finalVariants,
      conjugationAccompli: finalConjugationAccompli,
      conjugationInaccompli: finalConjugationInaccompli,
      playerId,
      createdAt: now,
      updatedAt: now,
    };
    currentWords.unshift(updatedWord);
  }

  const updatedList = filterOutListedWords(currentWords);
  inMemoryCache[playerId] = updatedList;

  if (typeof window !== 'undefined') {
    const key = getPlayerLocalStorageKey(playerId);
    try {
      localStorage.setItem(key, JSON.stringify(updatedList));
    } catch (e) {
      console.error('Error saving custom word to localStorage', e);
    }
    setStoredPlayerVocabInIDB(playerId, updatedList);

    window.dispatchEvent(
      new CustomEvent('kabyle_custom_words_updated', {
        detail: { playerId, words: updatedList },
      })
    );

    // Backup to server asynchronously
    fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ words: updatedList }),
    }).catch((err) => console.warn('Server player vocabulary backup warning:', err));
  }

  return { success: true, word: updatedWord };
}

// ==========================================
// Delete Single Word from Player's Account
// ==========================================
export function deleteCustomWord(wordIdOrKab: string, targetPlayerId?: string): boolean {
  const playerId = resolveCurrentPlayerId(targetPlayerId);
  const currentWords = getCustomWords(playerId);
  const filtered = currentWords.filter(
    (w) => w.id !== wordIdOrKab && w.kab.trim().toLowerCase() !== wordIdOrKab.trim().toLowerCase()
  );

  inMemoryCache[playerId] = filtered;

  if (typeof window !== 'undefined') {
    const key = getPlayerLocalStorageKey(playerId);
    try {
      localStorage.setItem(key, JSON.stringify(filtered));
    } catch (e) {
      console.error('Error saving custom word deletion', e);
    }
    setStoredPlayerVocabInIDB(playerId, filtered);

    window.dispatchEvent(
      new CustomEvent('kabyle_custom_words_updated', {
        detail: { playerId, words: filtered },
      })
    );

    fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ words: filtered }),
    }).catch((err) => console.warn('Server delete custom word sync warning:', err));
  }

  return true;
}

// ==========================================
// Clear All Custom Words for Player
// ==========================================
export function clearAllCustomWords(targetPlayerId?: string): void {
  const playerId = resolveCurrentPlayerId(targetPlayerId);
  inMemoryCache[playerId] = [];
  if (typeof window !== 'undefined') {
    const key = getPlayerLocalStorageKey(playerId);
    try {
      localStorage.removeItem(key);
    } catch {}
    clearStoredPlayerVocabFromIDB(playerId);
    window.dispatchEvent(
      new CustomEvent('kabyle_custom_words_updated', {
        detail: { playerId, words: [] },
      })
    );
    fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ words: [] }),
    }).catch(() => {});
  }
}

// ==========================================
// Sync Player Vocabulary with Server and Auto-Heal
// ==========================================
export async function syncPlayerVocabularyFromServer(targetPlayerId?: string): Promise<CustomWordItem[]> {
  if (typeof window === 'undefined') return [];
  const playerId = resolveCurrentPlayerId(targetPlayerId);

  try {
    const res = await fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`);
    if (res.ok) {
      const data = await res.json();
      const rawServerWords: CustomWordItem[] = (data && Array.isArray(data.words)) ? data.words : [];
      const serverWords = filterOutListedWords(rawServerWords);

      // Check IndexedDB and local memory
      const rawIdbWords = await getStoredPlayerVocabFromIDB(playerId);
      const idbWords = rawIdbWords ? filterOutListedWords(rawIdbWords) : null;
      const localWords = filterOutListedWords(inMemoryCache[playerId] || getCustomWords(playerId));

      const bestLocal = (idbWords && idbWords.length > 0) ? idbWords : localWords;

      // Smart merge: never lose words that exist locally or on server
      const wordMap = new Map<string, CustomWordItem>();
      bestLocal.forEach((w) => wordMap.set(w.kab.trim().toLowerCase(), w));
      serverWords.forEach((w) => wordMap.set(w.kab.trim().toLowerCase(), w));

      const mergedWords = Array.from(wordMap.values());

      if (serverWords.length === 0 && bestLocal.length > 0) {
        // Server container was rebooted and lost the file: Auto-heal to server!
        if (!isAutoHealingServer[playerId]) {
          isAutoHealingServer[playerId] = true;
          console.log(`Auto-healing custom vocabulary for player ${playerId} to server...`);
          fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ words: bestLocal }),
          })
            .catch((err) => console.warn('Auto-healing player vocab failed:', err))
            .finally(() => {
              isAutoHealingServer[playerId] = false;
            });
        }
      } else if (mergedWords.length > serverWords.length) {
        // Local had additional words not yet on server, push full merged list
        fetch(`/api/player-vocabulary/${encodeURIComponent(playerId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ words: mergedWords }),
        }).catch(() => {});
      }

      inMemoryCache[playerId] = mergedWords;
      const key = getPlayerLocalStorageKey(playerId);
      localStorage.setItem(key, JSON.stringify(mergedWords));
      await setStoredPlayerVocabInIDB(playerId, mergedWords);
      return mergedWords;
    }
  } catch (err) {
    console.warn('Could not sync player vocabulary from server, using local:', err);
  }

  // Fallback to IndexedDB / local cache
  const idbWords = await getStoredPlayerVocabFromIDB(playerId);
  if (idbWords && idbWords.length > 0) {
    inMemoryCache[playerId] = idbWords;
    return idbWords;
  }

  return getCustomWords(playerId);
}

// ==========================================
// Event Subscription Listener
// ==========================================
export function subscribeCustomWords(callback: (detail?: any) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => {
    callback((e as CustomEvent).detail);
  };
  window.addEventListener('kabyle_custom_words_updated', handler);
  return () => window.removeEventListener('kabyle_custom_words_updated', handler);
}

// Trigger initial sync for active player on script load
if (typeof window !== 'undefined') {
  const activeId = getActivePlayerId();
  syncPlayerVocabularyFromServer(activeId);
}
